// Kacheln des Teilnehmerrasters: Kamera/Avatar je Teilnehmer und eigene Kacheln für geteilte Bildschirme.
import type { Track } from 'livekit-client';
import { useEffect, useRef } from 'react';
import { getVoiceController } from './controller';
import { ExpandIcon, HeadphonesIcon, MicIcon } from './icons';
import { initialsOf, qualityBars, qualityLabel, type QualityLevel } from './logic';
import type { ParticipantView } from './snapshot';
import { useVoiceStore } from './store';

/** Hängt einen (Remote- oder lokalen) Videotrack an ein <video> an und löst ihn beim Abbau wieder. */
export function VideoView({ track, mirror, fit = 'cover', label }: { track: Track; mirror?: boolean; fit?: 'cover' | 'contain'; label: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, [track]);
  return (
    <video
      ref={ref}
      className={`lf-voice-video lf-voice-video--${fit}${mirror ? ' lf-voice-video--mirror' : ''}`}
      autoPlay
      playsInline
      muted
      aria-label={label}
    />
  );
}

export function Avatar({ name, url, size = 'lg' }: { name: string; url: string | null; size?: 'sm' | 'lg' }) {
  return (
    <span className={`lf-voice-avatar lf-voice-avatar--${size}`} aria-hidden="true">
      {url ? <img src={url} alt="" /> : initialsOf(name)}
    </span>
  );
}

export function QualityBars({ level, testId }: { level: QualityLevel; testId?: string }) {
  const bars = qualityBars(level);
  return (
    <span className={`lf-voice-quality lf-voice-quality--${level}`} title={`Verbindung: ${qualityLabel(level)}`} data-testid={testId} data-quality={level}>
      {[1, 2, 3].map((i) => (
        <span key={i} className={i <= bars ? 'is-on' : undefined} style={{ height: `${3 + i * 3}px` }} />
      ))}
      <span className="lf-voice-sr">Verbindung: {qualityLabel(level)}</span>
    </span>
  );
}

function VolumeSlider({ userId, name }: { userId: string; name: string }) {
  const volume = useVoiceStore((s) => s.volumes[userId] ?? 1);
  const pct = Math.round(volume * 100);
  return (
    <label className="lf-voice-volume" title={`Lautstärke ${pct} %`}>
      <span className="lf-voice-sr">Lautstärke von {name}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={pct}
        data-testid={`voice-volume-${userId}`}
        aria-valuetext={`${pct} %`}
        onChange={(e) => getVoiceController().setVolume(userId, Number(e.currentTarget.value) / 100)}
      />
    </label>
  );
}

export function ParticipantTile({ p }: { p: ParticipantView }) {
  const showVideo = p.hasVideo && p.cameraTrack;
  return (
    <div
      role="listitem"
      className={`lf-voice-tile${p.speaking ? ' is-speaking' : ''}${showVideo ? ' has-video' : ''}`}
      data-testid={`voice-tile-${p.userId}`}
      data-user-id={p.userId}
      data-local={p.isLocal ? 'true' : 'false'}
      data-has-video={p.hasVideo ? 'true' : 'false'}
      data-has-screen={p.hasScreen ? 'true' : 'false'}
      data-speaking={p.speaking ? 'true' : 'false'}
      data-muted={p.micMuted ? 'true' : 'false'}
      data-deafened={p.deafened ? 'true' : 'false'}
      aria-label={`${p.name}${p.isLocal ? ' (du)' : ''}${p.speaking ? ', spricht' : ''}${p.micMuted ? ', stumm' : ''}`}
    >
      <div className="lf-voice-tile__media">
        {showVideo && p.cameraTrack ? (
          <VideoView track={p.cameraTrack} mirror={p.isLocal} label={`Kamera von ${p.name}`} />
        ) : (
          <Avatar name={p.name} url={p.avatarUrl} />
        )}
      </div>
      <div className="lf-voice-tile__bar">
        <span className="lf-voice-tile__name">
          {p.name}
          {p.isLocal ? <span className="lf-voice-tile__you"> (du)</span> : null}
        </span>
        {p.hasScreen ? <span className="lf-voice-badge">Live</span> : null}
        {p.micMuted ? (
          <span className="lf-voice-tile__state" title="Stumm">
            <MicIcon off />
          </span>
        ) : null}
        {p.deafened ? (
          <span className="lf-voice-tile__state" title="Taub">
            <HeadphonesIcon off />
          </span>
        ) : null}
        <QualityBars level={p.quality} />
      </div>
      {!p.isLocal ? <VolumeSlider userId={p.userId} name={p.name} /> : null}
    </div>
  );
}

export function ScreenTile({ p }: { p: ParticipantView }) {
  const ref = useRef<HTMLDivElement>(null);
  const fullscreen = () => {
    const el = ref.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void el.requestFullscreen?.().catch(() => undefined);
  };
  if (!p.screenTrack) return null;
  return (
    <div ref={ref} className="lf-voice-screen" data-testid={`voice-screen-${p.userId}`} data-user-id={p.userId}>
      <VideoView track={p.screenTrack} fit="contain" label={`Bildschirm von ${p.name}`} />
      <div className="lf-voice-screen__bar">
        <span className="lf-voice-badge">Live</span>
        <span className="lf-voice-tile__name">{p.isLocal ? 'Dein Bildschirm' : `Bildschirm von ${p.name}`}</span>
        <button type="button" className="lf-voice-iconbtn" onClick={fullscreen} data-testid={`voice-screen-fullscreen-${p.userId}`} aria-label="Vollbild umschalten" title="Vollbild">
          <ExpandIcon />
        </button>
      </div>
    </div>
  );
}
